import Bugsnag, { NotifiableError } from '@bugsnag/js';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { RelatedProperty } from '../../../../types';
import { setStatus } from '../../../slices/contactForm';
import {
  closeMultipleLeadsSuccessModal,
  openMultipleLeadsSuccessModal,
  sendMultipleLeads
} from '../../../slices/multipleLeads';
import { RootState } from '../../../store';
import legacyProjectContactFormFetcher from '../../fetchers/legacyProjectContactFormFetcher';
import legacyTerrainContactFormFetcher, {
  LegacyTerrainContactFormFetcherArgs
} from '../../fetchers/legacyTerrainContactFormFetcher';
import fetchRelatedProperties from '../fetchers/fetchRelatedProperties';

export type StoreValues = Pick<
  LegacyTerrainContactFormFetcherArgs,
  'email' | 'name' | 'phone' | 'message'
> &
  Pick<RootState['multipleLeads'], 'relatedProperties' | 'propertyId' | 'type'>;

export const CONTACT_FORM_RECAPTCHA_ACTION = 'contactForm';

export function* watchSendMultipleLeadsWorker() {
  const values: StoreValues = yield select((state: RootState) => ({
    email: state.user.email.value,
    message: state.contactForm.message,
    name: state.user.name.value,
    phone: state.user.phone.value,
    relatedProperties: state.multipleLeads.relatedProperties,
    propertyId: state.multipleLeads.propertyId,
    type: state.multipleLeads.type
  }));

  if (window && window.grecaptcha) {
    for (const relatedProperty of values.relatedProperties) {
      try {
        const recaptchaToken: string = yield call(
          window.grecaptcha.execute,
          process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
          {
            action: CONTACT_FORM_RECAPTCHA_ACTION
          }
        );

        if (relatedProperty.type === 'projects') {
          if (!relatedProperty.communeId) {
            throw new Error(
              `"communeId" is missing in related property ${relatedProperty.id} (${relatedProperty.type})`
            );
          }

          yield call(legacyProjectContactFormFetcher, {
            email: values.email,
            message: values.message,
            name: values.name,
            phone: values.phone,
            projectCommuneId: relatedProperty.communeId,
            projectId: relatedProperty.id,
            recaptchaAction: CONTACT_FORM_RECAPTCHA_ACTION,
            recaptchaToken,
            source: 'multipleLeadsModal'
          });
        } else if (relatedProperty.type === 'terrains') {
          yield call(legacyTerrainContactFormFetcher, {
            code: relatedProperty.code,
            email: values.email,
            message: values.message,
            name: values.name,
            phone: values.phone,
            source: 'multipleLeadsModal',
            terrainId: relatedProperty.id,
            terrainTitle: relatedProperty.title,
            terrainUri: relatedProperty.url,
            recaptchaToken,
            recaptchaAction: CONTACT_FORM_RECAPTCHA_ACTION
          });
        }
      } catch (e) {
        console.error(e);
        Bugsnag.notify(e as NotifiableError);
      }
    }
  }

  const properties: Array<RelatedProperty> = yield call(
    fetchRelatedProperties,
    {
      email: values.email,
      propertyId: values.propertyId,
      type: values.type
    }
  );

  if (properties.length > 0) {
    yield all([
      put(openMultipleLeadsSuccessModal(properties)),
      put(setStatus('initial'))
    ]);
  } else {
    yield all([
      put(closeMultipleLeadsSuccessModal()),
      put(setStatus('success'))
    ]);
  }
}

export default function* watchSendMultipleLeads() {
  yield takeLatest(sendMultipleLeads, watchSendMultipleLeadsWorker);
}
